import { useMemo, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Search, X } from "lucide-react";

export const AttendeeSelector = ({
  users,
  attendees,
  label,
  onToggle,
  onGuestChange,
  onSetMany,
  testIdPrefix,
}) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      : users;
    // selected members first for quick review, then alphabetical
    return [...list].sort((a, b) => {
      const sa = attendees[a.id]?.selected ? 0 : 1;
      const sb = attendees[b.id]?.selected ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [users, query, attendees]);

  const visibleIds = filtered.map((u) => u.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => attendees[id]?.selected);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && query.trim() && filtered.length > 0) {
      e.preventDefault();
      const firstUnselected = filtered.find((u) => !attendees[u.id]?.selected) || filtered[0];
      onToggle(firstUnselected.id);
      setQuery("");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid={`${testIdPrefix}-select-all-btn`}
          onClick={() => onSetMany(visibleIds, !allVisibleSelected)}
          className="text-[#064E3B] hover:text-[#065f46]"
        >
          {allVisibleSelected ? "Deselect All" : "Select All"}
          {query.trim() ? " (shown)" : ""}
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid={`${testIdPrefix}-search-input`}
          placeholder="Search members by name..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-10 pl-9 pr-9 rounded-xl"
        />
        {query && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-search-clear`}
            onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div
        data-testid={`${testIdPrefix}-member-list`}
        className="border rounded-xl p-3 max-h-56 overflow-y-auto space-y-3"
      >
        {filtered.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-2" data-testid={`${testIdPrefix}-no-results`}>
            No members found
          </p>
        )}
        {filtered.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <Checkbox
                id={`${testIdPrefix}-${u.id}`}
                data-testid={`${testIdPrefix}-member-${u.id}`}
                checked={attendees[u.id]?.selected || false}
                onCheckedChange={() => onToggle(u.id)}
              />
              <label
                htmlFor={`${testIdPrefix}-${u.id}`}
                className="text-sm font-medium cursor-pointer truncate"
              >
                {u.name}
              </label>
            </div>
            {attendees[u.id]?.selected && (
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-500">Guests:</span>
                <Input
                  type="number"
                  min="0"
                  data-testid={`${testIdPrefix}-guests-${u.id}`}
                  value={attendees[u.id]?.guests || 0}
                  onChange={(e) => onGuestChange(u.id, e.target.value)}
                  className="w-16 h-8 text-center rounded-lg text-sm"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendeeSelector;
